import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/design-system/primitives'
import { useEventContent } from '@/features/event/api'
import { RubricPage } from '@/features/event/components/RubricPage'
import { siteText } from '@/features/event/siteTexts'

import { useCompetitionCandidates, useCompetitionCategories } from '../api'
import { CompetitionSection } from '../components'

/**
 * Les candidats validés, en public.
 *
 * La grille existait — photo, dossard en pastille, nom — et n'était montée sur
 * aucune page : elle avait été écrite puis oubliée. Les candidats n'étaient
 * donc visibles nulle part avant le soir de la soirée, alors que c'est
 * précisément ce qu'on veut montrer pendant les semaines qui précèdent.
 *
 * Seules les candidatures **approuvées** y figurent : l'API ne renvoie que
 * celles-là. Une candidature déposée n'est pas une candidature retenue, et
 * l'afficher reviendrait à publier quelqu'un que l'équipe n'a pas validé.
 */
export function CandidatesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    document.title = t('candidates.title')
  }, [t])

  return (
    <RubricPage title={t('candidates.title')} intro={t('candidates.intro')}>
      {(event) => <CandidatesBody slug={event.slug} onApply={navigate} />}
    </RubricPage>
  )
}

function CandidatesBody({
  slug,
  onApply,
}: {
  slug: string
  onApply: (path: string) => void
}) {
  const { t } = useTranslation()
  const categories = useCompetitionCategories(slug)
  const candidates = useCompetitionCandidates(slug)
  // Le titre de la section se réécrit dans Contenus (bloc `site.competition`).
  const content = useEventContent(slug)
  const heading = siteText(content.data, 'site.competition')

  if (categories.isPending || candidates.isPending) {
    return (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-72 w-full" />
        ))}
      </div>
    )
  }

  const rows = candidates.data ?? []
  if (rows.length === 0) {
    return <p className="max-w-xl text-content-secondary">{t('candidates.empty')}</p>
  }

  return (
    <CompetitionSection
      categories={categories.data ?? []}
      candidates={rows}
      title={heading ? heading.title : undefined}
      onApply={(categoryId) => onApply(`/concours?categorie=${categoryId}`)}
    />
  )
}
